import {
	useCallback, useEffect, useMemo, useState, useContext, useRef, KeyboardEvent,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { Shot, ShotFeed } from '../stores/types/shots';
import {
	FEED_TYPES, baseShot, baseShotData,
	baseShotFeed,
} from '../stores/reducers/shots';
import { GreenroomState as GRS } from '../stores/types/types';
import * as ShotsActions from '../stores/actions/shots';
import * as SettingsActions from '../stores/actions/settings';
import * as ModalActions from '../stores/actions/modal';
import * as MediaActions from '../stores/actions/media';
import type { Invitation, Profile } from '../stores/types/invitations';
import { ROOM_STATES } from '../stores/reducers/settings';
import Socket from '../connection/socket';
import { ConnectedUser } from '../stores/types/connection';
import { SocketContext } from '../contexts/socket';
import type { MediaItem, PresentedMediaItem } from '../stores/types/media';
import { showAlert } from '../components/general-components/alert-service';
import { MODAL_TEMPLATES } from '../stores/reducers/modal';
import {
	AddShot, EditShot, updateCurrentShotIdEntity,
} from '../connection/shots';
import { isVideo } from '../utils/utils';
import {
	setCurrentGraphicsId,
} from '../stores/actions/graphics';
import * as Alerts from '../components/general-components/alert-service';
import type { GraphicInterface } from '../stores/types/graphics';
import {
	maxOverlayAlertMs, maxOverlaysOnShot, MAX_AUDIO_ONLY_FEEDS, AUDIO_TYPES,
	HAS_AUDIO_TYPES, EPIDEMIC_VERSION_MAX_MS,
} from '../types/constants';
import { getFileExtension, getVideoDuration } from '../utils/media';
import { SaveSegment } from '../connection/segments';
import { getEffectiveShotDuration } from './use-effective-shot-duration';
import type { DragItem, DropCallbackData } from '../stores/types/user-interface';
import { DRAG_TYPE } from '../stores/reducers/user-interface';
import { ResourcePoolContext } from './use-resource-pool';
import { setBroadcastStartingShot } from '../stores/actions/broadcasting';
import { VideoStream } from '../components/types/videos';
import usePrevious from '../utils/use-previous';
import { ShotsContextInterface } from '../contexts/shots/provider';
import useAutoplay from './use-autoplay';
import { useVideoMachine } from '../contexts/video-machine';
import { validateShotData } from '../connection/utils';
import {
	useAutoplayOnScheduledStream,
	useOpenShotModal,
} from './use-shot-utilities';
import { useTimingLeader } from './use-timing-leader';
import { saveTitleAsOverlay } from '../utils/custom-graphics';
import useInitOverlayTransform from './use-init-overlay-transform';
import { editOverlayDraft } from '../connection/graphics';
import { CreateMedia, CreateMediaFromGenerateContentSound, UpsertMediaDraft, UpsertRecentSlideDeck } from '../connection/media';
import SoundVersionManager from '../utils/sound/sound-manager';
import { nonEditTypes, STATIC_ASSET_URL } from '../utils/content-templates';
import { setSelectedMediaTemplate } from '../stores/actions/generate-content';
import { closeMetaMenu, setActiveSubTab, setActiveTab, setRightDrawerOpen } from '../stores/actions/tabs';
import { TABS } from '../stores/types/tabs';
import { EntityType } from '../types/enums';
import changeShotLayout from '../utils/changeShotLayout';
import V_LAYOUTS from '../utils/video-layout';

// buffer to block toggling autoplay within a few seconds of the shot ending
import { AUTOPLAY_SECOND_BUFFER } from './use-shot-utilities';
import useShotMemos from './use-shot-memos';
import useMediaItemMemos from './use-media-memos';
import { MEDIA_SETTINGS_TABS } from '../stores/types/session';
import { ALERT_TYPES } from '../components/general-components/alert-types';

const noAOFMediaFeeds = (feed: ShotFeed) => !(feed.audioOnly && (feed.feedType === FEED_TYPES.MEDIA || feed.feedType === FEED_TYPES.MEDIA_AUDIO))

const useShotsCustomHook = (): ShotsContextInterface => {
	const dispatch = useDispatch();
	const shots = useSelector((s: GRS) => s.ShotsReducer.shots);
	const currentShotId = useSelector((s: GRS) => s.ShotsReducer.currentShotId);
	const shotModalShot = useSelector((s: GRS) => s.ShotsReducer.shotModalShot);
	const shotTabShotId = useSelector((s: GRS) => s.ShotsReducer.shotTabShotId);
	const shotIdToDelete = useSelector((s: GRS) => s.ShotsReducer.shotIdToDelete);
	const invitations = useSelector((s: GRS) => s.InvitationsReducer.invitations);
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const session = useSelector((s: GRS) => s.SessionReducer.session);
	const meeting = useSelector((s: GRS) => s.SessionReducer.meeting);
	const connectedUsers = useSelector((s: GRS) => s.SessionReducer.connectedUsers);
	const webrtcID = useSelector((s: GRS) => s.SessionReducer.webrtcID);
	const screenShareWebrtcId = useSelector((s: GRS) => s.SessionReducer.screenShareWebrtcId);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const media = useSelector((s: GRS) => s.MediaReducer.media);
	const tabsState = useSelector((s: GRS) => s.TabsReducer);
	const presentedMedia = useSelector((s: GRS) => s.MediaReducer.presentedMedia);
	const roomState = useSelector((s: GRS) => s.MeetingReducer.roomState);
	const clearShot = useSelector((s: GRS) => s.SettingsReducer.clearShot);
	const modalOpen = useSelector((s: GRS) => s.ModalReducer.modalOpen);
	const modalTemplate = useSelector((s: GRS) => s.ModalReducer.modalTemplate);
	const graphics = useSelector((s: GRS) => s.GraphicsReducer.graphics);
	const shotStartTimestampEntity = useSelector(
		(s: GRS) => s.ShotsReducer.shotStartTimestampEntity?.entityValue?.start,
	);
	const shotModalGraphics = useSelector((s: GRS) => s.GraphicsReducer.shotModalGraphics);
	const brdcstStartShotId = useSelector((s: GRS) => s.BroadcastReducer.broadcastStartingShotId);
	const connectedPresenterVideos = useSelector((s: GRS) => s.VideoReducer.connectedPresenterVideos);
	const connectedMediaVideos = useSelector((s: GRS) => s.VideoReducer.connectedMediaVideos);
	const deletedShotOrder = useSelector((s: GRS) => s.ShotsReducer.deletedShotOrder);
	const currentShotIdEntity = useSelector((s: GRS) => s.ShotsReducer.currentShotIdEntity);
	const enabledGraphics = useSelector((s: GRS) => s.GraphicsReducer.enabledGraphics);
	const scheduledStream = useSelector((s: GRS) => s.MeetingReducer.scheduledStream);
	const distributedShotDurations = useSelector((s: GRS) => s.SegmentsReducer.distributedShotDurations);
	const segments = useSelector((s: GRS) => s.SegmentsReducer.segments);
	const recentSlideDeckEntity = useSelector((s: GRS) => s.MediaReducer.recentSlideDeckEntity);
	const generatedContentTemplates = useSelector((s: GRS) => s.GeneratedContentReducer.contentTemplates);
	const meetingId = useSelector((s: GRS) => s.SessionReducer.meeting?.meeting_id);

	const { 
		use_title_library: useTitleLibrary,
		use_new_shot_thumbs: useNewShotThumbs,
		use_segments: useSegments,
	} = useSelector((s: GRS) => s.SettingsReducer.featureFlags);

	const { videoMachineState } = useVideoMachine();
	const { socket } = useContext(SocketContext);

	const {
		currentShot,
		nextShot,
		isOnCanvas,
		isShotBuilderOpen,
		getShotDuration,
	} = useShotMemos();

	const previousShotId = usePrevious(currentShotId);

	const currentShotDuration = useMemo(() =>
		getEffectiveShotDuration(currentShot, useSegments, distributedShotDurations),
	[useSegments, currentShot.shot_id, currentShot.shot_data.shotDuration, distributedShotDurations]);

	const { remainingTimeInShot } = useAutoplay();

	const handleOpenShotModal = useOpenShotModal();

	const handleAutoplayOnScheduledStream = useAutoplayOnScheduledStream();

	const isTimingLeader = useTimingLeader();

	const [backupShot, setBackupShot] = useState<Shot>(shots ? shots[0] : baseShot);
	const [isNextShot, setIsNextShot] = useState<boolean>(true);

	// State value only used by showboard-drawer currently
	const setNextShotValue = (value: boolean): void => {
		setIsNextShot(value);
	};

	const resourcePool = useContext(ResourcePoolContext);

	const getShotHasMediaVideo = (selectedShot: Shot, currentMediaArray: MediaItem[]): boolean => {
		const { shot_data: { shotFeeds } } = selectedShot;

		let i = 0;
		const len = shotFeeds.length;
		for (i; i < len; i += 1) {
			const shotFeed = shotFeeds[i];
			const mediaItem = currentMediaArray.find((m) => m.id === shotFeed.media);
			const presentedMediaItem = presentedMedia.find((m) => m.mediaId === shotFeed.media);

			if (mediaItem) {
				const {
					media_data: { image_array: imageArray },
				} = mediaItem;

				if (imageArray.length) {
					const currentPage = presentedMediaItem?.currentPage;
					const image = currentPage
						? imageArray[currentPage - 1]
						: imageArray[0];

					if (image && isVideo(image)) {
						return true;
					}
				}
			}
		}

		return false;
	};

	// Global shot graphics based on if shot modal is open or closed
	const globalShotGraphics = useMemo((): number[] => {
		const allGraphics = isShotBuilderOpen ? shotModalGraphics : graphics;

		return allGraphics.reduce<number[]>(
			(idArr: number[], graphic: GraphicInterface) => {
				const { id } = graphic;

				if (id && graphic?.is_global) {
					return [...idArr, id];
				}

				return idArr;
			},
			[],
		);
	}, [graphics, isShotBuilderOpen, shotModalGraphics]);

	// Combines global graphics with current shot or modal shot graphics
	const combinedShotGraphics = useMemo((): number[] => {
		const shotGraphics = isShotBuilderOpen
			? shotModalShot.shot_data.shotGraphics ?? []
			: currentShot.shot_data.shotGraphics ?? [];

		return Array.from(new Set([...shotGraphics, ...globalShotGraphics]));
	}, [
		currentShot,
		globalShotGraphics,
		isShotBuilderOpen,
		shotModalShot,
	]);

	const combinedShotGraphicsIsMax = useMemo((): boolean => {
		if (combinedShotGraphics.length >= maxOverlaysOnShot) {
			return true;
		}

		return false;
	}, [combinedShotGraphics]);

	/*
	This is used to check if we can add a global graphic to other shots.
	The reason for not checking the current shot is that converting to a
	global graphic is more of an edit than an add for the current shot.
	*/
	const otherShotGraphicsIsMax = useMemo((): boolean => {
		let isMax = false;
		shots.forEach((shot) => {
			const otherShotGraphics = shot.shot_id !== currentShotId
				? shot.shot_data.shotGraphics ?? []
				: [];
			const allShotGraphics = Array.from(
				new Set([...otherShotGraphics, ...globalShotGraphics]),
			);

			if (allShotGraphics.length >= maxOverlaysOnShot) {
				isMax = true;
			}
		});

		return isMax;
	}, [currentShotId, globalShotGraphics, shots]);

	const editShotModalShot = useCallback((
		newShotModalShot: Shot,
		socket: Socket | null,
	) => {
		dispatch(ShotsActions.setShotModalShot(newShotModalShot));
		// send socket message for all other editing modals that are open to this shot
		socket?.sendMessage('update-modal-shot', { shot: newShotModalShot, from: session });
	}, [session]);

	// A method to set the shared media for a shot.
	// Should be called when presentedMedia should be updated for the current shot,
	// but not when the shot is changing since that is handled by Beehive.
	const setSharedMediaForShot = useCallback(({ shot_data: { shotFeeds } }: Shot) => {
		// Update the presentedMedia array for Showtime, but not Showbuilder.
		if (roomState !== ROOM_STATES.SHOWTIME) {
			return;
		}

		// Determine if we need to send a new presentedMedia or if the
		// same media is shared before/after the shot has updated.
		const mediaIdsInNewFeeds = shotFeeds
			.map((shotFeed: ShotFeed) => shotFeed.media)
			.filter((id) => !!id);
		const mediaIdsInLastFeeds = currentShot.shot_data.shotFeeds
			.map((shotFeed: ShotFeed) => shotFeed.media)
			.filter((id) => !!id);

		// Remove media that not in the shot feeds.
		const newPresentedMedia = [...presentedMedia].filter(
			(med: PresentedMediaItem) => !!mediaIdsInNewFeeds?.includes(med.mediaId),
		);

		// Persist slide data in presentedMediaItems to track current page number
		const slideMediaNotInShot = presentedMedia.filter((med: PresentedMediaItem) => {
			if (!mediaIdsInNewFeeds?.includes(med.mediaId)) {
				const mediaItem = media.find((m) => m.id === med.mediaId);

				return (mediaItem?.media_data?.image_array?.length ?? 0) > 1;
			}

			return false;
		});

		// Add new media to presentedMedia
		shotFeeds.forEach((feed) => {
			const newFeed: ShotFeed = { ...feed };

			// if media feed, handle presenting media
			if (newFeed.feedType === FEED_TYPES.MEDIA) {
				const mediaIsAlreadyShared = newPresentedMedia.find(
					(med: PresentedMediaItem) => med.mediaId === newFeed.media,
				);
				const mediaItem = media.find(
					(med: MediaItem) => med.id === newFeed.media,
				);

				if (!mediaIsAlreadyShared) {
					// If the media item isn't shared, go find it and add it to the
					// new sharedMediaArr.

					if (mediaItem) {
						// Copy any data from the media item that is used during presenting
						const mediaToPresent: PresentedMediaItem = {
							mediaId: mediaItem.id,
							currentPage: 1,
						};

						newPresentedMedia.push(mediaToPresent);
					}
				} else if (
					// If the media is slides and wasn't in last shot, then unpause it.
					mediaIsAlreadyShared
					&& (mediaItem?.num_pages ?? 0) > 1
					&& !mediaIdsInLastFeeds?.includes(newFeed.media)
				) {
					const index = newPresentedMedia.findIndex(
						(med: PresentedMediaItem) => med.mediaId === newFeed.media,
					);
					const videoState = newPresentedMedia?.[index]?.videoState;

					if (index > -1 && videoState) {
						videoState.paused = !shotStartTimestampEntity;
						newPresentedMedia[index].videoState = videoState;
					}
				}
			}
		});

		// Update the presentedMedia array
		dispatch(MediaActions.setPresentedMedia([
			...newPresentedMedia,
			...slideMediaNotInShot.map((med) => {
				if (med.videoState) {
					return {
						...med,
						videoState: {
							paused: true,
							timeElapsed: 0,
							volume: med.videoState?.volume ?? 1,
							muted: med.videoState.muted ?? false,
						},
					};
				}

				return med;
			}),
		]));
	}, [media, roomState, presentedMedia]);

	const sessionSocketId = useMemo(() => {
		const userObj = connectedUsers.find((u) => u.gr_user === user?.gr_user);
		return userObj?.socketId ?? '';
	}, [connectedUsers, user]);

	// handleSelectShot is called when a video ends.
	// All users run this code, but only the timing leader should update the current shot id entity.
	// Non-leaders will receive the update via socket when the leader updates Redis.
	const handleSelectShot = useCallback(async (
		newShotId: number,
		isPlaying: boolean = true,
		socket?: Socket | null,
		shouldUpdateRedis: boolean = true,
	) => {
		if (!token
			|| !meeting?.meeting
			|| !currentShotIdEntity
			|| Number.isNaN(newShotId)
		) return;

		const newEntity = { ...currentShotIdEntity };
		newEntity.entityValue.currentShotId = newShotId;

		// Only update Redis if shouldUpdateRedis is true AND (in SHOWTIME OR switching to live)
		// This allows timing leader to broadcast, while non-leaders just update locally
		if (shouldUpdateRedis && (roomState === ROOM_STATES.SHOWTIME || !isPlaying)) {
			updateCurrentShotIdEntity(token, meeting.meeting, newEntity, sessionSocketId, isPlaying);
			if (socket && previousShotId) {
				dispatch(ShotsActions.loadShot(token, meeting.meeting, previousShotId));
			}
		} else {
			console.log('handleSelectShot => non-timing leader dispatching locally');
			dispatch({ type: newEntity.entityType, payload: newEntity });
		}
	}, [currentShotIdEntity, token, meeting?.meeting, roomState, shotStartTimestampEntity, isTimingLeader, sessionSocketId, previousShotId]);

	const finalizeDropIntoShot = useCallback(async (
		isForShotEditorModal: boolean,
		newShot: Shot,
		socket: Socket,
		newMedia?: MediaItem,
		skipThumbnailRegen: boolean = false,
	) => {
		if (token && !isForShotEditorModal) {
			const newShotAutoplay = handleAutoplayOnScheduledStream(newShot);

			await EditShot(token, newShotAutoplay, meeting?.meeting_id, meeting?.channel, useNewShotThumbs, skipThumbnailRegen);

			// Ensure that presentedMedia is updated when a media item is
			// dropped into a shot.
			setSharedMediaForShot(newShotAutoplay);
		} else {
			const validatedShot = JSON.parse(JSON.stringify(newShot));

			// redux may not have the new item when dropping new generated media
			const newMediaArray = media.map((item) => item);
			if (newMedia && !media.find((m) => m.id === newMedia.id)) newMediaArray.push(newMedia);

			validatedShot.shot_data = await validateShotData(validatedShot.shot_data, newMediaArray);
			editShotModalShot(validatedShot, socket);
		}
	}, [
		token,
		handleAutoplayOnScheduledStream,
		handleSelectShot,
		editShotModalShot,
		media,
		useNewShotThumbs,
	]);

	const {
		currentTemplate,
		currentTemplateMediaItem,
		currentAudioMediaItem,
	} = useMediaItemMemos();

	const feedDroppedOnCanvas = useCallback(async (
		item: DragItem,
		socket: Socket | null,
		isForShotEditorModal: boolean,
		feedPosition: number,
		audioOnly?: boolean,
	): Promise<null> => {
		if (!socket) return null;
		// todo alert for no socket connection

		const itemToDrop: DragItem = JSON.parse(JSON.stringify(item));

		let audioMediaItem = null;

		let template = '';
		let drawerItem = null;
		// media from a content template needs to be created in the database first
		if (item.template) {
			if (!meeting || !user || !token || typeof item.id !== 'string') {
				return null;
			}

			const mediaItem = {
				id: 1,
				created_by: user.gr_user,
				original_name: item.name ?? '',
				thumbnail_status: 1,
				location: '',
				meeting: meeting.meeting,
				type: getFileExtension(item.id),
				images: item.id,
				endpoint: `${STATIC_ASSET_URL}/title_templates/${item.template.replace('brandlive-', '')}`,
				cloud_convert_id: null,
				template_type: item.template,
				provider: item.provider,
				media_data: {
					image_array: [item.id],
					thumbnail_map: {},
					duration_map: [],
				},
			} as unknown as MediaItem;

			// Populate duration_map for videos
			if (isVideo(item.id) && resourcePool) {
				const videoElement = document.createElement('video');
				videoElement.muted = true;
				try {
					const duration = await getVideoDuration(
						item.id,
						mediaItem.type,
						videoElement,
						resourcePool,
					);
					// Push duration to array at index 0 (corresponds to first item in image_array)
					if (mediaItem.media_data.duration_map) {
						mediaItem.media_data.duration_map.push(duration);
					}
				} catch (e) {
					console.error(`Failed to get duration for ${item.id}:`, e);
					// Continue without duration - not critical
				}
			}

			const newMedia: MediaItem[] = await CreateMedia(token, mediaItem);

			if (newMedia.length === 0) {
				return null;
			}

			itemToDrop.id = newMedia[0].id;

			drawerItem = newMedia[0];
			template = item.template;

			if (meetingId && item.data?.stylePack) {
				let targetDuration = Math.floor(mediaItem.media_data.duration_map?.[0] || currentShotDuration || item.data?.data?.duration);
				let matchingItems;
				if (targetDuration) {
					//attempt to find a audio version that matches duration
					matchingItems = generatedContentTemplates.filter(x => x.stylePack == item.data?.stylePack && x.contentType && x.contentType.toString() == "audio_version" && x.data?.config?.duration == targetDuration);

					// if the item duration exceeds the max epidemic version duration, prefer loopable
					const audioDuration = matchingItems?.[0]?.data?.duration;
					const shouldUseLoopable = matchingItems?.length > 0 && audioDuration && targetDuration > EPIDEMIC_VERSION_MAX_MS / 1000;

					if (matchingItems?.length == 0 || shouldUseLoopable) {
						//attempt to find a audio version (loopable) that matches duration
						const loopableItems = generatedContentTemplates.filter(x => x.stylePack == item.data?.stylePack && x.contentType && x.contentType.toString() == "audio_version_loopable" && x.data?.config?.duration == targetDuration);
						if (loopableItems?.length > 0) matchingItems = loopableItems;
					}

					if (matchingItems?.length == 0) {
						//since a version is not found, find a whole track instead
						matchingItems = generatedContentTemplates.filter(x => x.stylePack == item.data?.stylePack && x.contentType && x.contentType.toString() == 'audio');
					}

					if (matchingItems?.length) {
						let audioItem = matchingItems[0];
						if (audioItem && audioItem.videoUrl) {
							audioMediaItem = (await CreateMediaFromGenerateContentSound(token, meetingId, audioItem.type, audioItem.data?.id)) as MediaItem;
						}
					}
				}
			}

		}

		const { type, id } = itemToDrop;
		const mediaItem = media.find((med: MediaItem) => med.id === Number(id));

		if (mediaItem?.template_type) {
			drawerItem = mediaItem;
			template = mediaItem.template_type;
		}

		if (
			drawerItem
			&& template
			&& !nonEditTypes.includes(template)
			&& roomState === ROOM_STATES.EDITING
			&& !item.data?.data?.strict //if NOT a non-customizable stylepack, allow this menu
		) {
			dispatch(setCurrentGraphicsId(0));
			dispatch(setSelectedMediaTemplate(drawerItem, template));
			dispatch(setRightDrawerOpen(true));
			dispatch(setActiveTab(TABS.GENERATE_MEDIA_CONTENT));
			dispatch(closeMetaMenu());
		}

		if (AUDIO_TYPES.includes(mediaItem?.type ?? '') && !audioOnly) {
			console.error('cannot drop this type of media here');
			return null;
		}

		let editedShot: Shot = JSON
			.parse(JSON.stringify(isForShotEditorModal ? shotModalShot : currentShot));

		const newFeed = {
			position: audioOnly ? editedShot.shot_data.shotFeeds.length : feedPosition,
			feedType: type,
			media: null,
			presenter: null,
			email: null,
			screenshare: null,
			sipStreamId: null,
			srtStreamId: null,
			onsiteLocationId: null,
			audioOnly,
		} as ShotFeed;

		let newAudioFeed = null;
		if (audioMediaItem) {
			newAudioFeed = {
				position: editedShot.shot_data.shotFeeds.length,
				feedType: FEED_TYPES.MEDIA_AUDIO,
				media: audioMediaItem.id,
				audioOnly: true,
			} as ShotFeed;
		}

		if (type === DRAG_TYPE.FEED_MEDIA || type === DRAG_TYPE.FEED_MEDIA_AUDIO) {
			newFeed.media = Number(id);

			if (audioOnly && useTitleLibrary) {
				// filter out other AOF media items
				editedShot.shot_data.shotFeeds = editedShot.shot_data.shotFeeds.filter(noAOFMediaFeeds);

				//if NOT a non-customizable stylepack, allow this menu
				if (!item.data?.data?.strict)
				{
					if (roomState === ROOM_STATES.EDITING && mediaItem && HAS_AUDIO_TYPES.includes(mediaItem.type)) {
						if (currentTemplate && currentTemplateMediaItem) {
							dispatch(setSelectedMediaTemplate(currentTemplateMediaItem, currentTemplateMediaItem.template_type ?? ''));
							dispatch(setActiveTab(TABS.GENERATE_MEDIA_CONTENT));
							dispatch(setActiveSubTab(TABS.GENERATE_MEDIA_CONTENT, MEDIA_SETTINGS_TABS.CONTENT));
						} else {
							dispatch(setSelectedMediaTemplate(mediaItem, mediaItem.type));
							dispatch(setActiveTab(TABS.AUDIO_SETTINGS));
						}
						dispatch(setRightDrawerOpen(true));
					}
				}
			}

			if (isForShotEditorModal && meeting) {
				const mediaDraft = drawerItem ? drawerItem : mediaItem;
				if (mediaDraft) {
					const upserted = await UpsertMediaDraft(meeting.meeting, mediaDraft);
					if (upserted && drawerItem) {
						dispatch(setSelectedMediaTemplate(drawerItem, drawerItem.template_type ?? ''));
					}
				}
			}
		}

		if (type === DRAG_TYPE.FEED_PRESENTER) { newFeed.presenter = Number(id); }

		if (type === DRAG_TYPE.FEED_EMAIL) { newFeed.email = String(id); }

		if (type === DRAG_TYPE.FEED_SCREENSHARE) { newFeed.screenshare = String(id); }

		if (type === DRAG_TYPE.FEED_SIP_CALL) { newFeed.sipStreamId = String(id); }

		if (type === DRAG_TYPE.FEED_SRT_STREAM) { newFeed.srtStreamId = Number(id); }

		if (type === DRAG_TYPE.FEED_ONSITE_LOCATION) { newFeed.onsiteLocationId = Number(id); }

		// check max audio only shots
		if (
			newFeed.audioOnly
			&& editedShot.shot_data.shotFeeds
				.filter((shotFeed) => shotFeed.audioOnly).length === MAX_AUDIO_ONLY_FEEDS) {
			return null;
		}

		let newFeedPreviousIndex = -1;
		let stylepackMedia = 0;

		// track if we removed the selected template item from the shot
		const selectedTemplateItemMediaId = currentTemplateMediaItem?.id ?? null;
		let selectedItemWasRemovedFromShot = false;

		// This is for unique media items
		editedShot.shot_data.shotFeeds
			.forEach((feed: ShotFeed, index: number) => {
				// Find matching feeds in a different position to remove them
				if (
					((feed.media && feed.media === newFeed.media)
						|| (feed.presenter && feed.presenter === newFeed.presenter)
						|| (feed.email && feed.email === newFeed.email)
						|| (feed.screenshare && feed.screenshare === newFeed.screenshare)
						|| (feed.sipStreamId && feed.sipStreamId === newFeed.sipStreamId)
						|| (feed.srtStreamId && feed.srtStreamId === newFeed.srtStreamId)
						|| (feed.onsiteLocationId && feed.onsiteLocationId === newFeed.onsiteLocationId))
					&& index !== newFeed.position
				) {
					if (feed.audioOnly) {
						editedShot.shot_data.shotFeeds.splice(index, 1);
					} else if (newFeed.audioOnly) {
						editedShot.shot_data.shotFeeds[index] = {
							position: index,
							feedType: DRAG_TYPE.FEED_EMPTY,
							media: null,
							presenter: null,
							email: null,
							screenshare: null,
						} as ShotFeed;
					} else {
						newFeedPreviousIndex = index;
					}
				} else if (feed.audioOnly && feed.feedType === DRAG_TYPE.FEED_EMPTY) {
					// Remove empty audio only feeds. We ignore them in audio-only-feeds.tsx but they
					// need to be removed when this function is run.
					editedShot.shot_data.shotFeeds.splice(index, 1);
				}

				if (feed.media && feed.feedType === DRAG_TYPE.FEED_MEDIA) {
					const item = media.find((x: MediaItem) => x.id == feed.media);
					if (item?.provider) {
						stylepackMedia++;
					}
					if (item?.id === selectedTemplateItemMediaId && feedPosition === index) {
						selectedItemWasRemovedFromShot = true;
					}
				}
			});

		// we keep all the audio only feeds at the end so they don't show up on canvas
		// since shotFeeds is an array and not a map
		if (audioOnly) {
			editedShot.shot_data.shotFeeds[editedShot.shot_data.shotFeeds.length] = newFeed;
		} else if (editedShot.shot_data.shotFeeds[feedPosition].audioOnly) {
			editedShot.shot_data.shotFeeds.splice(feedPosition, 0, newFeed);
		} else {
			//if user is dropping a stylepack item onto a multi source shot, change it to a 1up
			if (feedPosition > 0 && item.template && stylepackMedia) {
				const newShot = changeShotLayout(editedShot, V_LAYOUTS.ONE.FULL, 1);
				if (newShot) {
					editedShot = newShot;
					feedPosition = 0;
				}
			} else if (newFeedPreviousIndex > -1) {
				const oldFeed = editedShot.shot_data.shotFeeds[feedPosition];
				if (oldFeed) { // oldFeed should exist here (may have feedType == DRAG_TYPE.FEED_EMPTY)
					oldFeed.position = newFeedPreviousIndex;
					editedShot.shot_data.shotFeeds[newFeedPreviousIndex] = oldFeed;
					if (selectedItemWasRemovedFromShot && oldFeed.media === selectedTemplateItemMediaId) {
						selectedItemWasRemovedFromShot = false;
					}
				}
			}
			editedShot.shot_data.shotFeeds[feedPosition] = newFeed;
		}

		// if new item is not a stylepack and selected item removed, de-select
		if (!template && selectedItemWasRemovedFromShot) {
			dispatch(setSelectedMediaTemplate(null, ''));
		}

		if (newAudioFeed) {
			// filter out other AOF media items
			editedShot.shot_data.shotFeeds = editedShot.shot_data.shotFeeds.filter(noAOFMediaFeeds);
			editedShot.shot_data.shotFeeds.push(newAudioFeed);
		}

		await finalizeDropIntoShot(
			isForShotEditorModal,
			editedShot,
			socket,
			drawerItem ?? undefined,
			!!audioOnly,
		);

		return null;
	}, [
		shotModalShot,
		currentShot,
		token,
		handleSelectShot,
		editShotModalShot,
		finalizeDropIntoShot,
		getShotDuration,
		currentTemplateMediaItem,
	]);

	const pushShotFeedToCurrentShot = async (
		feed: ShotFeed,
		audioOnly?: boolean,
	) => {
		const editedShot: Shot = JSON.parse(JSON.stringify(currentShot));
		const lastFeedIndex = editedShot.shot_data.shotFeeds.length - 1;
		const lastFeed = editedShot.shot_data.shotFeeds[lastFeedIndex];

		if (lastFeed && lastFeed.audioOnly) {
			if (audioOnly) {
				feed.position = lastFeedIndex;
				editedShot.shot_data.shotFeeds[lastFeedIndex] = feed;
			} else {
				feed.position = editedShot.shot_data.shotFeeds.length - 1;
				editedShot.shot_data.shotFeeds.splice(-1, 0, feed);
			}
		} else {
			feed.position = editedShot.shot_data.shotFeeds.length;
			editedShot.shot_data.shotFeeds.push(feed);
		}

		if (socket) {
			await finalizeDropIntoShot(false, editedShot, socket);
		}
	};

	const getShotKey = (item: ShotFeed): number | string | null => {
		const {
			media: mediaId,
			presenter,
			email,
			screenshare,
			feedType,
		} = item;
		let id;

		if (feedType === DRAG_TYPE.FEED_MEDIA || feedType === DRAG_TYPE.FEED_MEDIA_AUDIO) {
			id = Number(mediaId);
		} else if (feedType === DRAG_TYPE.FEED_PRESENTER) {
			id = Number(presenter);
		} else if (feedType === DRAG_TYPE.FEED_EMAIL) {
			id = String(email);
		} else if (feedType === DRAG_TYPE.FEED_SCREENSHARE) {
			id = String(screenshare);
		} else if (feedType === DRAG_TYPE.FEED_SIP_CALL) {
			id = String(item.sipStreamId);
		} else if (feedType === DRAG_TYPE.FEED_SRT_STREAM) {
			id = String(item.srtStreamId);
		} else if (feedType === DRAG_TYPE.FEED_ONSITE_LOCATION) {
			id = String(item.onsiteLocationId);
		} else {
			// eslint-disable-next-line no-console
			console.warn(`Unable to decern shot item type! Skipping. ${JSON.stringify(item)}`);
			id = null;
		}

		return id;
	};

	const setFeedExpanded = useCallback((feed: ShotFeed, socket: Socket, expanded: boolean, isForShotEditorModal: boolean) => {
		if (!token || !socket) return;

		const editedShot: Shot = JSON.parse(JSON.stringify(isForShotEditorModal ? shotModalShot : currentShot));
		const feedIndex = editedShot.shot_data.shotFeeds.findIndex(
			(feedItem) => getShotKey(feedItem) === getShotKey(feed),
		);

		if (feedIndex >= 0) {
			editedShot.shot_data.shotFeeds[feedIndex].expanded = expanded;
		}

		if (isForShotEditorModal) {
			editShotModalShot(editedShot, socket);
		} else {
			dispatch(ShotsActions.editShot(token, editedShot, meeting?.meeting_id, meeting?.channel, useNewShotThumbs));
		}
	}, [token, currentShot, shotModalShot, meeting, useNewShotThumbs]);

	const removeFeedFromShot = useCallback(async (
		deleteItem: ShotFeed,
		socket: Socket,
		isForShotEditorModal: boolean,
	): Promise<null> => {
		const shot: Shot = JSON.parse(
			JSON.stringify(isForShotEditorModal
				? shotModalShot
				: currentShot),
		);

		// Remove selected element
		const deleteId = getShotKey(deleteItem);
		const filteredShotFeeds = shot.shot_data.shotFeeds.filter(
			(feedItem) => (getShotKey(feedItem) !== deleteId),
		);
		shot.shot_data.shotFeeds = filteredShotFeeds;

		finalizeDropIntoShot(isForShotEditorModal, shot, socket, undefined, !!deleteItem.audioOnly);

		return null;
	}, [shotModalShot, currentShot, finalizeDropIntoShot, getShotDuration]);

	const checkMaxOverlays = useCallback((isForShotEditorModal: boolean) => {
		const editedShotGraphics = isForShotEditorModal
			? shotModalShot.shot_data.shotGraphics
			: currentShot.shot_data.shotGraphics;

		const updatedGraphicArray = [...(editedShotGraphics ?? []), ...globalShotGraphics];

		if (updatedGraphicArray.length >= maxOverlaysOnShot) {
			Alerts.showAlert({
				message: 'Cannot add overlay',
				description: "You've reached the maximum number of overlays for this shot",
				duration: maxOverlayAlertMs,
			});
			// We do not want any changes at this point
			return true;
		}

		if (updatedGraphicArray.length === maxOverlaysOnShot - 1) {
			Alerts.showAlert({
				message: 'Maximum Overlay Number Reached',
				description: `Only ${maxOverlaysOnShot} overlays are allowed per shot`,
				duration: maxOverlayAlertMs,
			});
		}

		return false;
	}, [shotModalShot, currentShot, globalShotGraphics]);

	const { calcPositionOnAdd } = useInitOverlayTransform();

	const overlayDroppedOnCanvas = useCallback(
		async (
			item: DragItem,
			socket: Socket | null,
			isForShotEditorModal: boolean,
			dropData: DropCallbackData,
			newOverlay?: GraphicInterface,
		): Promise<null> => {
			await calcPositionOnAdd(item, dropData.relativePosition);

			if (!socket) return null;
			// todo alert for no socket connection
			const id = Number(item.id);

			const usedShot = isForShotEditorModal ? shotModalShot : currentShot;
			const editedShot: Shot = JSON.parse(JSON.stringify(usedShot));

			// new title overlays are checked for max overlays in titleDroppedOnCanvas
			if (item.type === DRAG_TYPE.OVERLAY) {
				const maxExceeded = checkMaxOverlays(isForShotEditorModal);

				if (maxExceeded) {
					return null;
				}
			}

			if (editedShot.shot_data.shotGraphics?.includes(id) || globalShotGraphics?.includes(id)) {
				Alerts.showAlert({
					message: 'Cannot add overlay',
					description: 'This overlay is already on the shot',
					duration: maxOverlayAlertMs,
				});
				return null;
			}

			// if shot didn't have overlay already in it, add it
			if (!editedShot.shot_data.shotGraphics?.includes(id)) {
				if (editedShot.shot_data.shotGraphics) {
					editedShot.shot_data.shotGraphics.push(id);
				} else {
					(editedShot.shot_data.shotGraphics = [id]);
				}
			}

			if (!isForShotEditorModal && roomState !== ROOM_STATES.EDITING) {
				handleOpenShotModal(editedShot, socket);

				// Now that we are editing using the modal, must set flag for saving
				// eslint-disable-next-line no-param-reassign
				isForShotEditorModal = true;
			}

			if (isForShotEditorModal) {
				// title generation passes in an overlay that is not yet part of the graphics array
				const usedOverlay = newOverlay ?? graphics.find((g) => g.id === id);
				if (usedOverlay) {
					editOverlayDraft(token ?? '', usedOverlay);
				}
			}

			dispatch(setCurrentGraphicsId(id));
			dispatch(setSelectedMediaTemplate(null, ''));
			if (!isForShotEditorModal) {
				// edit modal has a useEffect in shot-edit-ui.tsx instead
				dispatch(setActiveTab(TABS.OVERLAYS));
				dispatch(setRightDrawerOpen(true));
				dispatch(closeMetaMenu());
			}

			finalizeDropIntoShot(isForShotEditorModal, editedShot, socket);

			return null;
		},
		[
			shotModalShot,
			currentShot,
			token,
			handleSelectShot,
			editShotModalShot,
			finalizeDropIntoShot,
			handleOpenShotModal,
			graphics,
			globalShotGraphics,
		],
	);

	const titleDroppedOnCanvas = useCallback(
		async (
			item: DragItem,
			socket: Socket | null,
			isForShotEditorModal: boolean,
			dropData: DropCallbackData,
		): Promise<null> => {
			const maxExceeded = checkMaxOverlays(isForShotEditorModal);

			if (maxExceeded) {
				return null;
			}

			// We just dropped a title on the canvas
			// we want to save the title as an overlay to the db
			// and then add it to the shot
			saveTitleAsOverlay(
				item.name!,
				(item.id as string),
				dispatch,
				user,
				meeting!,
				(newOverlay: GraphicInterface) => overlayDroppedOnCanvas(
					{ ...item, id: newOverlay.id ?? 0 },
					socket,
					isForShotEditorModal,
					dropData,
					newOverlay,
				),
				token,
				enabledGraphics,
				item.template,
				item.provider,
				dropData.relativePosition,
			);

			return null;
		},
		[
			shotModalShot,
			currentShot,
			token,
			enabledGraphics,
			user,
			meeting,
			overlayDroppedOnCanvas,
			graphics,
			globalShotGraphics,
		],
	);

	const resetFeed = (
		inputId: number | string | null,
		inputType: string,
		feed: ShotFeed,
		altId: number | string | null,
	): ShotFeed => {
		let id = inputId;
		let type = inputType;

		// we also want to reset any corresponding screenshare feeds for presenters
		if (feed.feedType === FEED_TYPES.SCREENSHARE && type === FEED_TYPES.PRESENTER) {
			id = altId;
			type = FEED_TYPES.SCREENSHARE;
		}

		const typeMatch = feed.feedType === type;
		const idMatch = (
			(type === FEED_TYPES.MEDIA_AUDIO && feed.media === id)
			|| (type === FEED_TYPES.MEDIA && feed.media === id)
			|| (type === FEED_TYPES.PRESENTER && feed.presenter === id)
			|| (type === FEED_TYPES.SCREENSHARE && feed.screenshare === id)
			|| (type === FEED_TYPES.SIP_CALL && feed.sipStreamId === id)
			|| (type === FEED_TYPES.SRT_STREAM && feed.srtStreamId === id)
			|| (type === FEED_TYPES.ONSITE_LOCATION && feed.onsiteLocationId === id)
		);
		if (typeMatch && idMatch) {
			return {
				...baseShotFeed,
				position: feed.position,
				audioOnly: !!feed.audioOnly,
			};
		}
		return feed;
	};

	const stripFeedFromShots = (
		id: number | string | null,
		type: string,
		isForShotEditorModal: boolean = false,
		altId: number | string | null = null,
	) => {
		const editedShots = shots.reduce((acc: Shot[], shot: Shot) => {
			let wasEdited = false;
			const newShot: Shot = {
				...shot,
				shot_data: {
					...shot.shot_data,
					shotFeeds: shot.shot_data.shotFeeds.map((feed) => {
						const newFeed = resetFeed(id, type, feed, altId);
						if (feed.feedType !== newFeed.feedType) wasEdited = true;
						return newFeed;
					}),
				},
			};
			if (wasEdited) acc.push(newShot);
			return acc;
		}, []);

		if (token && editedShots.length > 0) {
			dispatch(ShotsActions.bulkEditShots(token, editedShots));
		}

		if (isForShotEditorModal) {
			dispatch(ShotsActions.setShotModalShot({
				...shotModalShot,
				shot_data: {
					...shotModalShot.shot_data,
					shotFeeds: shotModalShot.shot_data.shotFeeds.map((f) => resetFeed(id, type, f, altId)),
				},
			}));
		}
	};

	const findClosestShotInOrder = (
		shotsArr: Shot[],
		baseShotId: number,
		shotOrder?: number | null,
	): Shot | null => {
		// finds the closest shot to the given baseShotId in order, prioritizes shots after
		let thisShotOrder = shotOrder;

		if (!shotOrder) {
			const thisShot = shotsArr.find((s) => s.shot_id === baseShotId);

			thisShotOrder = thisShot?.shot_data.order;
		}

		if (thisShotOrder) {
			const shotOrderNumber: number = thisShotOrder;
			const isFirstShot: boolean = currentShot.shot_data.order === 0;
			const shotInOrder = shotsArr.find(
				(shot) => shot.shot_data.order === shotOrderNumber,
			);

			let newShot = isFirstShot
				? shotInOrder
				: currentShot;

			if (!newShot) {
				newShot = shotsArr.find(
					(shot) => shot.shot_data.order === shotOrderNumber - 1,
				);
			}

			return newShot ?? shotsArr[0] ?? null;
		}

		return shotsArr[0] ?? null;
	};

	const handleDeletingShot = useCallback((): void => {
		// ok it took me a second to figure out why this is weird.
		// because we have so many local items (currentshot, shottabmodalshot,brdcstStartShotId)
		// we need to update everyone's local store with a new shot to use
		if (!token || !meeting || !shotIdToDelete) return;

		const localShotIdToDelete = shotIdToDelete;
		const localDeletedShotOrder = deletedShotOrder;

		dispatch(ShotsActions.setShotIdToDelete(null));
		dispatch(ShotsActions.setDeletedShotOrder(null));

		// this useEffect should only run when we received a socket message that set `shotIdToDelete`

		// select new shot if deleted shot === current shot
		if (localShotIdToDelete === currentShotId) {
			const newCurrShot = findClosestShotInOrder(
				shots,
				currentShotId,
				localDeletedShotOrder,
			);

			if (newCurrShot) {
				handleSelectShot(newCurrShot.shot_id);
			}
		}

		// handle if deleted shot === shot tab shot
		if (localShotIdToDelete === shotTabShotId) {
			/*
				For the shot tab:
				When deleting a shot, return to the current shot if the new
				shot was not switched to. Otherwise, switch to the next shot.
			*/
			const adjustOrderBy = shotTabShotId === currentShotId ? 0 : 1;
			const newTabShot = findClosestShotInOrder(
				shots,
				shotTabShotId,
				localDeletedShotOrder ? localDeletedShotOrder - adjustOrderBy : null,
			);

			if (newTabShot) {
				dispatch(ShotsActions.setShotTabShotId(newTabShot.shot_id));
			}
		}

		// handle if deleted shot === shot modal shot
		if (localShotIdToDelete === shotModalShot.shot_id) {
			// users with the modal open will change to the generic "Shot Deleted" page
			// this also accounts for the delete modal closing
			dispatch(ShotsActions.setShotModalShot(baseShot));

			// if another user has the delete shot modal open
			if (modalOpen && modalTemplate === MODAL_TEMPLATES.DELETE_SHOT_CONFIRMATION) {
				dispatch(ModalActions.setModalOpen(false));
			}
		}

		// handle deleting recording shot
		if (localShotIdToDelete === brdcstStartShotId) {
			const newBroadcastShot = findClosestShotInOrder(
				shots,
				brdcstStartShotId,
				localDeletedShotOrder,
			);

			if (newBroadcastShot) {
				dispatch(setBroadcastStartingShot(newBroadcastShot.shot_id));
			}
		}
	}, [
		token,
		meeting,
		shotIdToDelete,
		currentShotId,
		shotModalShot.shot_id,
		shots,
		handleSelectShot,
		brdcstStartShotId,
		deletedShotOrder,
		modalOpen,
		modalTemplate,
	]);

	const prevShotIdToDelete = usePrevious(shotIdToDelete);
	useEffect(() => {
		// this runs like 3x for every time it should, so be warned
		// ive tried to stop/rework it several times now
		if (shotIdToDelete && shotIdToDelete !== prevShotIdToDelete) {
			ShotsActions.setShotIdToDelete(null);
			handleDeletingShot();
		}
	}, [shotIdToDelete, prevShotIdToDelete, handleDeletingShot]);

	const handleAddingShot = useCallback(
		async (newShotPartial: Shot): Promise<Shot | null> => {
			if (!token || !session) {
				return null;
			}

			const shotData = {
				...baseShotData,
				...newShotPartial.shot_data,
			};

			const newShot: Shot = {
				...newShotPartial,
				shot_data: shotData,
			};

			const addedShot = await AddShot(token, newShot, session);
			dispatch(ShotsActions.addShot(addedShot));

			return addedShot ?? null;
		},
		[token, session],
	);

	const changeShotTitle = useCallback((newTitle: string, shotId: number) => {
		if (!token) return;

		const match = shots.find((shot: Shot) => shot.shot_id === shotId);
		if (match) {
			const shotNewTitle: Shot = {
				...match,
				shot_data: {
					...match.shot_data,
					shotTitle: newTitle,
				},
			};
			dispatch(ShotsActions.editShot(token, shotNewTitle, meeting?.meeting_id, meeting?.channel, useNewShotThumbs, true));
		}
	}, [shots, token, meeting, useNewShotThumbs]);

	const changeShotDuration = useCallback((
		newDuration: number,
		shotBeingEdited: Shot,
		// eslint-disable-next-line no-undef
		durationRef: React.MutableRefObject<HTMLInputElement | null>,
		isForShotEditorModal: boolean,
		socket: Socket | null,
	) => {
		if (!token) return;

		const hasEpidemicAudio = shotBeingEdited?.shot_data?.shotFeeds?.some(feed => {
			if (!feed.media) return false;
			const mediaItem = media.find(m => m.id === feed.media);
			return mediaItem?.provider === 'epidemic sound' && mediaItem?.template_type;
		});

		const effectiveShotBeingEditedDuration = getEffectiveShotDuration(shotBeingEdited, useSegments, distributedShotDurations);
		if (hasEpidemicAudio && newDuration !== effectiveShotBeingEditedDuration) {
			const epidemicMediaItem = shotBeingEdited?.shot_data?.shotFeeds?.find(feed => {
				if (!feed.media) return false;
				const mediaItem = media.find(m => m.id === feed.media);
				return mediaItem?.provider === 'epidemic sound' && mediaItem?.template_type;
			});

			if (epidemicMediaItem) {
				const mediaItem = media.find(m => m.id === epidemicMediaItem.media);

				if (mediaItem?.template_type) {
					const soundManager = SoundVersionManager.getInstance();
					soundManager.fetchVersions(token, mediaItem.template_type, newDuration, {
						onComplete: () => {
							const isAnyAudioTabOpen = tabsState.rightDrawerOpen &&
														(tabsState.activeTab === TABS.AUDIO_SETTINGS ||
															(tabsState.activeTab === TABS.GENERATE_MEDIA_CONTENT &&
															tabsState.activeSubTabs[TABS.GENERATE_MEDIA_CONTENT] === MEDIA_SETTINGS_TABS.AUDIO));
							if (!isAnyAudioTabOpen) {
								showAlert({
									message: "Audio changed to match new duration",
									description: "You can choose from new versions now",
									type: ALERT_TYPES.INFO,
									duration: 5000,
									actions: [{
										title: 'Review',
										 onClick: () => {
      										dispatch(setSelectedMediaTemplate(mediaItem, mediaItem.template_type ?? ''));

											dispatch(setRightDrawerOpen(true));

											if (currentTemplate) {
												dispatch(setActiveTab(TABS.GENERATE_MEDIA_CONTENT));
												dispatch(setActiveSubTab(TABS.GENERATE_MEDIA_CONTENT, MEDIA_SETTINGS_TABS.AUDIO));
											} else {
												dispatch(setActiveTab(TABS.AUDIO_SETTINGS));
											}
										}
									}]
								});
							}
						}
					}, meeting?.meeting_id, mediaItem.id);
				}
			}
		}

		const shotNewDuration: Shot = {
			...shotBeingEdited,
			shot_data: {
				...shotBeingEdited.shot_data,
				shotDuration: newDuration,
				autoplay: newDuration
					? shotBeingEdited.shot_data.autoplay
					: false,
			},
		};

		if (isForShotEditorModal) {
			editShotModalShot(shotNewDuration, socket);
		} else {
			const shotNewDurationAutoplay = handleAutoplayOnScheduledStream(
				shotNewDuration,
			);

			dispatch(ShotsActions.editShot(token, shotNewDurationAutoplay, meeting?.meeting_id, meeting?.channel, useNewShotThumbs, true));
		}
	}, [
		editShotModalShot,
		token,
		meeting,
		meetingId,
		media,
		scheduledStream,
		handleAutoplayOnScheduledStream,
		tabsState,
		useNewShotThumbs,
		useSegments,
		segments,
		distributedShotDurations,
	]);

	const currentShotGraphics = useMemo((): GraphicInterface[] => {
		const currentShotGraphicIds: number[] = currentShot?.shot_data?.shotGraphics ?? [];

		return (
			graphics.reduce(
				(allGraphicsArray: GraphicInterface[], graphic: GraphicInterface) => {
					const { id, is_global: isGlobal } = graphic;

					// If graphic is in current shot or is global
					if (
						id
						&& (currentShotGraphicIds.includes(id) || !!isGlobal)
						&& !allGraphicsArray.map(({ id: graphicId }) => graphicId).includes(id)
					) {
						return [...allGraphicsArray, graphic];
					}

					return allGraphicsArray;
				},
				[],
			) ?? []
		);
	}, [currentShot, graphics]);

	const playShot = useCallback((shotId: number, socket: Socket) => {
		const shot = shots.find((_shot) => _shot.shot_id === shotId);

		if (!shot || !socket) {
			return;
		}

		handleSelectShot(shot.shot_id);

		if (clearShot) {
			dispatch(SettingsActions.setClearShot(false));
		}
	}, [
		handleSelectShot,
		session,
		shots,
		clearShot,
		shotStartTimestampEntity,
	]);

	/*
		this is used in a live showboard to detect users in a shot that currently aren't
		in the room and empty feeds. it will return an array of identifiers based on the
		feed type and text for what user data is available.
	*/
	const getMissingFeeds = useCallback((shotFeeds: ShotFeed[]): string => {
		// user to not show these icons until the user is connected and in the room
		const connectedSelf = connectedUsers.find(
			(_user: ConnectedUser) => _user.webrtcID === webrtcID && _user.in_room,
		);
		if (!connectedSelf) return '';

		const missingFeedList = shotFeeds.map((shotFeed: ShotFeed): string | undefined => {
			// Empty audioOnly feeds are allowed in the AOF drawer until a user triggers a save
			// and they get purged. Because of this, we need to prevent the missing
			// feeds notification from showing due to audio only feeds being empty.
			if (shotFeed.audioOnly && shotFeed.feedType === 'empty') return undefined;
			if (
				shotFeed.feedType === FEED_TYPES.PRESENTER
				&& !connectedUsers.find(
					(_user: ConnectedUser) => _user.gr_user === shotFeed.presenter && _user.in_room,
				)
			) {
				const invite = invitations.find(
					(i: Invitation) => i.profile.gr_user === shotFeed.presenter,
				);
				if (invite && invite.profile.first_name && invite.profile.last_name) {
					return `${invite.profile.first_name} ${invite.profile.last_name}`;
				} if (invite && invite.profile.first_name) {
					return `${invite.profile.first_name}`;
				} if (invite && invite.profile.email) {
					return `${invite.profile.email}`;
				}
			} else if (
				shotFeed.feedType === FEED_TYPES.EMAIL
				&& shotFeed.email
				&& !connectedUsers.find(
					(_user: ConnectedUser) => _user.email === shotFeed.email && _user.in_room,
				)
			) {
				return shotFeed.email;
			} else if (
				shotFeed.feedType === FEED_TYPES.SCREENSHARE
				&& shotFeed.screenshare
				&& !connectedUsers.find(
					(connectedUser: ConnectedUser) => connectedUser.email === shotFeed.screenshare
					&& connectedUser.screenShareWebrtcId,
				)
			) {
				return '';
			} else if (shotFeed.feedType === FEED_TYPES.EMPTY) {
				return '';
			}
			return undefined;
		}).filter((feed: string | undefined) => typeof feed === 'string');

		if (missingFeedList.length === 1) {
			return !missingFeedList[0] ? 'Missing feed or media' : `${missingFeedList[0]} is missing`;
		} if (missingFeedList.length === 2) {
			// there are several variations of text we want to show for 2 missing feeds
			// 2 empty feeds - 'Missing feeds or media'
			// 2 missing presenters - 'PRESENTER NAME and PRESENTER NAME are missing'
			// 1 empty feed and 1 missing presenter. empty feed should always be first -
			// 'Missing feed or media and PRESENTER NAME is missing'
			if (!missingFeedList[0] && !missingFeedList[1]) {
				return 'Missing feeds or media';
			} if (missingFeedList[0] && missingFeedList[1]) {
				return `${missingFeedList[0]} and ${missingFeedList[1]} are missing`;
			}
			const emptyFirst = !missingFeedList[0]
				? [...missingFeedList]
				: [...missingFeedList].reverse();
			return `Missing feed or media & ${emptyFirst[1]} is missing`;
		} if (missingFeedList.length > 2) {
			return `${missingFeedList.length} missing items`;
		}
		return '';
	}, [connectedUsers, invitations, webrtcID]);

	const hiddenTwoUpUserProfile = useCallback((shotFeeds: ShotFeed[]): Profile | null => {
		const userId = shotFeeds[0]?.presenter;
		const userEmail = shotFeeds[0]?.email;

		let connectedUser;
		let invitedUser;

		if (userId) {
			connectedUser = connectedUsers.find(
				(_user: ConnectedUser) => _user.gr_user === userId,
			);
			invitedUser = invitations.find(
				(invite: Invitation) => invite.profile.gr_user === userId,
			);
		} else if (userEmail) {
			connectedUser = connectedUsers.find(
				(_user: ConnectedUser) => _user.email === userEmail,
			);
			invitedUser = invitations.find(
				(invite: Invitation) => invite.profile.email === userEmail,
			);
		}

		if (connectedUser) {
			return {
				...connectedUser,
				poster: connectedUser.poster ?? undefined,
				picture: '',
				job_title: '',
			};
		} if (invitedUser) {
			return invitedUser.profile;
		}

		return null;
	}, [connectedUsers, invitations]);

	const idsOfVideosInCanvas = useMemo(() => {
		const newIdsOfVideosInCanvas = [] as string[];

		currentShot.shot_data.shotFeeds.forEach((feed) => {
			if (feed.media) {
				const connectedVideoForMedia = connectedMediaVideos.find(
					(vid: VideoStream) => vid.mediaId === feed.media,
				);
				if (connectedVideoForMedia) { newIdsOfVideosInCanvas.push(connectedVideoForMedia.id); }
			}

			if (feed.presenter) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.profile === feed.presenter,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				}
			}

			if (feed.screenshare) {
				const connectedUser = connectedUsers
					.find((_connectedUser) => _connectedUser.email === feed.screenshare);

				if (connectedUser) {
					const connectedVideoForPresenter = connectedPresenterVideos.find(
						(vid: VideoStream) => vid.id === connectedUser.screenShareWebrtcId,
					);

					if (connectedVideoForPresenter) {
						newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
					}
				}
			}

			if (feed.sipStreamId) {
				const connectedVideoForPresenter = connectedPresenterVideos.find(
					(vid: VideoStream) => vid.id === feed.sipStreamId,
				);
				if (connectedVideoForPresenter) {
					newIdsOfVideosInCanvas.push(connectedVideoForPresenter.id);
				}
			}
		});

		return newIdsOfVideosInCanvas;
	}, [connectedPresenterVideos, connectedMediaVideos, currentShot.shot_data.shotFeeds]);

	const connectedUser = useMemo(() => (
		connectedUsers
			.find((_connectedUser) => user?.gr_user && _connectedUser.gr_user === user.gr_user)
	), [user?.gr_user, connectedUsers]);

	const currentShotAutoGraphicIds = useMemo((): number[] => (
		currentShotGraphics.reduce(
			(graphicIdArray: number[], { id, animation }: GraphicInterface) => {
				if (id && animation && animation.build_in !== 1) {
					return [...graphicIdArray, id];
				}

				return graphicIdArray;
			},
			[],
		) ?? []
	), [currentShotGraphics]);

	// keep track of if the current shot has played through its duration
	const [lockAutoPlay, setLockAutoPlay] = useState<boolean>(false);
	const interval = useRef<NodeJS.Timeout>();

	const checkAndSetPlayedThrough = (
		duration: number,
		autoDuration: boolean,
		startTime: Date | null,
		videoIsEnding: boolean,
	) => {
		const secondsRemaining = remainingTimeInShot(
			startTime,
			duration,
			autoDuration,
			videoIsEnding ? AUTOPLAY_SECOND_BUFFER : AUTOPLAY_SECOND_BUFFER * AUTOPLAY_SECOND_BUFFER,
		);

		if (secondsRemaining === null) {
			setLockAutoPlay(false);
		} else if (secondsRemaining < AUTOPLAY_SECOND_BUFFER) {
			clearInterval(interval.current);
			setLockAutoPlay(true);
		} else {
			setLockAutoPlay(false);
		}
	};

	const videoIsEnding = useMemo(
		() => (videoMachineState.duration - videoMachineState.elapsed) < AUTOPLAY_SECOND_BUFFER,
		[videoMachineState.elapsed, videoMachineState.duration],
	);

	const handleSaveOnEnter = useCallback(
		(event: KeyboardEvent<HTMLInputElement>, editFn?: () => any) => {
			if (event.key === 'Enter') {
				event.preventDefault();
				event.currentTarget?.blur();
				if (editFn) {
					editFn();
				}
			}
		},
		[],
	);

	useEffect(() => {
		clearInterval(interval.current);
		interval.current = setInterval(() => {
			checkAndSetPlayedThrough(
				currentShotDuration,
				currentShot.shot_data.autoDuration,
				shotStartTimestampEntity,
				videoIsEnding,
			);
		}, 500);
		return () => clearInterval(interval.current);
	}, [
		currentShot.shot_data.autoDuration,
		currentShotDuration,
		shotStartTimestampEntity,
		videoIsEnding,
	]);

	useEffect(() => {
		if (meeting && roomState === ROOM_STATES.SHOWTIME) {
			const { shotFeeds } = currentShot.shot_data;
			for (let i = 0; i < shotFeeds.length; i += 1) {
				// don't proceed if shot feed has no media id
				if (shotFeeds[i].media) {
					const slideDeck = media.find((m) => m.id === shotFeeds[i].media && m.num_pages > 1);
					// if media is a slide deck, upsert as most recent slide deck entity
					if (slideDeck) {
						if (slideDeck.id !== recentSlideDeckEntity?.entityValue?.mediaId) {
							UpsertRecentSlideDeck(
								meeting.meeting,
								slideDeck.id,
								recentSlideDeckEntity?.version,
							);
						}
						// we want to break out of the loop if we find a deck
						break;
					}
				}
			}
		}
	}, [
		roomState,
		currentShot,
		media,
	]);

	return {
		handleSelectShot,
		overlayDroppedOnCanvas,
		feedDroppedOnCanvas,
		removeFeedFromShot,
		stripFeedFromShots,
		editShotModalShot,
		handleAddingShot,
		changeShotTitle,
		changeShotDuration,
		currentShot,
		nextShot,    
		playShot,
		getMissingFeeds,
		getShotHasMediaVideo,
		isShotBuilderOpen,
		hiddenTwoUpUserProfile,
		globalShotGraphics,
		combinedShotGraphics,
		combinedShotGraphicsIsMax,
		otherShotGraphicsIsMax,
		idsOfVideosInCanvas,
		isOnCanvas,
		isNextShot,
		setNextShotValue,
		currentShotGraphics,
		currentShotAutoGraphicIds,
		getShotDuration,
		lockAutoPlay,
		titleDroppedOnCanvas,
		checkMaxOverlays,
		setFeedExpanded,
		handleSaveOnEnter,
		pushShotFeedToCurrentShot,
	};
};

export default useShotsCustomHook;
